import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import "../styles/animations.css";

const ACCENT = "#F2B84B";

type Bullet = { label: string; detail: string };

type Job = {
  company: string;
  url?: string;
  /** Logo shown on a white badge in the job detail header. */
  logo?: string;
  location: string;
  title: string;
  date: string;
  bullets: Bullet[];
};

type Education = {
  school: string;
  url?: string;
  location: string;
  degree: string;
  date: string;
  focus: string;
  minor?: string;
};

const LINKEDIN_URL = "https://www.linkedin.com/in/waltyao/";
const GITHUB_URL = "https://github.com/walter-kai";

const workExperience: Job[] = [
  {
    company: "Hootsuite",
    url: "https://www.hootsuite.com",
    logo: "/logos/work/hootsuite-owl.png",
    location: "Vancouver, BC",
    title: "Technical Partner Manager",
    date: "June 2023 – Current",
    bullets: [
      {
        label: "Marketplace Manager",
        detail:
          "Approves all apps for the marketplace, scouts for prospective integrations and builds relationships with other Partner Managers",
      },
      {
        label: "Contract Management",
        detail:
          "Overseeing a portfolio of integrations built by external contractors; performs regular usage tracking and contractor review processes",
      },
      {
        label: "Developer Experience",
        detail:
          "Chief Editor of Hootsuite's Developer docs website, coordinator of all technical comms to external developers",
      },
      {
        label: "Technical SME",
        detail:
          "Company expert for API, MCP, apps and integrations. Works with external clients and internal colleagues to develop ideal plans that work for both sales and product teams",
      },
      {
        label: "Client Consulting",
        detail:
          "Advises enterprise clients, including banks, government, energy and retail companies, on API/MCP integration strategy and optimization",
      },
      {
        label: "Support Efficiency",
        detail:
          "Completely turned the developer support desk into a functional service, reducing support ticket volume by 96% and cutting SLA response times from weeks to hours",
      },
    ],
  },
  {
    company: "Tenamint",
    location: "Toronto, ON",
    title: "Chief Strategy Officer",
    date: "September 2023 – October 2025",
    bullets: [
      {
        label: "Strategic CTO",
        detail:
          "Total management of the engineering team — Scrummaster, developer, and project manager for a Web3 Fantasy Sports website",
      },
      {
        label: "Hands-On Full-Stack Dev",
        detail:
          "Built the initial front end in React with a Python backend, later migrating the full stack to Next.js with Node",
      },
      {
        label: "DevOps",
        detail:
          "Optimized system architecture to divide up subsystems into individual Lambda-like instances",
      },
      {
        label: "Leadership",
        detail:
          "Spearheaded product and operations management, defining the long-term roadmap",
      },
      {
        label: "Partner Management",
        detail:
          "Developed networks within the crypto industry, such as NEAR blockchain integrations",
      },
      {
        label: "Growth",
        detail:
          "Led product marketing initiatives and sprint planning to align technical execution with business goals; developed Product Marketing plans to coordinate features to a set event schedule",
      },
    ],
  },
  {
    company: "DLive.tv",
    url: "https://dlive.tv",
    logo: "/logos/work/DLive-logo.png",
    location: "Taipei, Taiwan",
    title: "Group Product Manager",
    date: "May 2022 – March 2024",
    bullets: [
      {
        label: "Leadership",
        detail:
          "Managed a cross-functional team of 25 across engineering, product, customer service, and marketing departments for live streaming site, DLive.tv",
      },
      {
        label: "Product Development",
        detail:
          "Oversaw the Web2 to Web3 migration across the platform, primarily focused on monetization schemes",
      },
      {
        label: "Product Scrummaster",
        detail:
          "Wrote and executed PRDs, developed long-term roadmaps, and led the daily scrums with 20+ engineers",
      },
      {
        label: "Management",
        detail:
          "Conducted interviews and restructured teams across 3 departments to focus on new initiatives",
      },
      {
        label: "Content Cultivator",
        detail:
          "Developed and executed a scouting, onboarding, and tracking system for new content creators, used by a team of 4 marketers",
      },
    ],
  },
  {
    company: "Evolution Gaming",
    url: "https://www.evolution.com",
    logo: "/logos/work/evolution.png",
    location: "Taipei",
    title: "Lead Technical Project Manager",
    date: "March 2021 – May 2022",
    bullets: [
      {
        label: "Leadership",
        detail:
          "Mentored a team of five project managers and implemented process improvements across 3 departments",
      },
      {
        label: "Architect",
        detail:
          "Primary director of the customer technical onboarding process, developing process and reviewing team performance",
      },
      {
        label: "Facilitator",
        detail:
          "Conducted weekly check-ins with teammates, assisting in providing the tools and resources needed",
      },
      {
        label: "API Prototyping",
        detail:
          "Built working casino game instances in JavaScript and React, later migrated to Next.js, with a Python Flask backend, to test and validate REST API integrations firsthand",
      },
      {
        label: "Optimizer",
        detail:
          "Managed 50+ REST API integration projects to onboard new clients to a high-volume gaming platform",
      },
      {
        label: "Strategic Consulting",
        detail:
          "Provided technical and business consultation to global clients on market trends and placement",
      },
    ],
  },
  {
    company: "Eli Lilly & Co",
    url: "https://www.lilly.com",
    logo: "/logos/work/eli-lilly.svg",
    location: "Taipei",
    title: "Product Owner",
    date: "February 2020 – March 2021",
    bullets: [
      {
        label: "Primary Architect",
        detail:
          "Spearheaded a physician-facing video-on-demand platform, doubling active users within 6 months by adding quality-of-life features for a more personalized experience",
      },
      {
        label: "Technical PRDs",
        detail:
          "Gathered user stories to define technical PRDs, working directly with engineers while ensuring all features are compliant within the pharmaceutical industry",
      },
      {
        label: "PowerApps Guru",
        detail:
          "Built a series of interactive compliancy forms used for tracking contractors",
      },
    ],
  },
  {
    company: "Arup",
    url: "https://www.arup.com",
    logo: "/logos/work/arup.png",
    location: "Los Angeles / Toronto",
    title: "Audiovisual and IT Consultant",
    date: "October 2016 – 2020",
    bullets: [
      {
        label: "Owner's Representative",
        detail:
          "Enterprise-level audiovisual (AV) systems for commercial buildings and transportation",
      },
      {
        label: "Clients",
        detail:
          "Microsoft, Kaiser Permanente, The University of Southern California, Toronto Pearson Airport, WeWork",
      },
      {
        label: "Stakeholder Presentations",
        detail: "Delivered concept presentations to facility stakeholders",
      },
      {
        label: "Estimating",
        detail: "Developed ROM estimates for projects throughout North America",
      },
      {
        label: "Procurement",
        detail: "Created RFP documents and conducted bid analysis",
      },
      {
        label: "Delivery",
        detail: "Project management and construction administration",
      },
      {
        label: "Design",
        detail: "Custom AV design for large-scale commercial projects",
      },
      {
        label: "Master Planning",
        detail: "Roadmaps for standard AV designs",
      },
    ],
  },
  {
    company: "DSI Luxury Technology",
    location: "Los Angeles",
    title: "Crestron Integration Programmer",
    date: "November 2014 – July 2016",
    bullets: [
      {
        label: "Automation Programming",
        detail:
          "Developer of residential automation control programming for Crestron and Savant system projects",
      },
      {
        label: "UI Design",
        detail: "Touch panel interface design",
      },
      {
        label: "Drivers",
        detail: "Device driver development (AV, lighting, alarm, HVAC)",
      },
      {
        label: "Client Consulting",
        detail: "Client consultations for custom software programming",
      },
    ],
  },
  {
    company: "TMMI",
    location: "Vancouver",
    title: "C++ Software Developer",
    date: "September 2013 – November 2014",
    bullets: [
      {
        label: "Video Compression",
        detail: "Video compression technology",
      },
      {
        label: "Windows GUI",
        detail: "Windows 32 API GUI development",
      },
      {
        label: "Image Formats",
        detail:
          "Decoding/encoding PNG, BMP, RGB, TIFF, DNG, and DPX image formats",
      },
      {
        label: "Distributed Computing",
        detail: "C++ and Ruby distributed computing management",
      },
    ],
  },
  {
    company: "C3 - Custom Code Crafters",
    location: "Essex / London, United Kingdom",
    title: "Project Manager Programmer",
    date: "March 2009 – September 2013",
    bullets: [
      {
        label: "Automation Programming",
        detail:
          "Developer of residential automation control programming for Crestron and Savant system projects",
      },
      {
        label: "UI Design",
        detail: "Touch panel interface design",
      },
      {
        label: "Drivers",
        detail: "Device driver development (AV, lighting, alarm, HVAC)",
      },
      {
        label: "Client Consulting",
        detail: "Client consultations for custom software programming",
      },
    ],
  },
  {
    company: "Visual Defence / PJS Systems",
    location: "Burnaby, BC",
    title: "Crestron Programmer / Project Lead",
    date: "March 2008 – March 2009",
    bullets: [
      {
        label: "Automation Software",
        detail:
          "Developed automation control software for corporate and government facilities",
      },
      {
        label: "UI Design",
        detail: "Touch panel interface design",
      },
      {
        label: "Drivers",
        detail: "Device driver development (AV, lighting, alarm, HVAC)",
      },
      {
        label: "Client Consulting",
        detail: "Client consultations for custom software programming",
      },
    ],
  },
  {
    company: "EngSoft Systems",
    location: "Surrey, BC",
    title: "C Programmer",
    date: "October 2006 – January 2008",
    bullets: [
      {
        label: "CAD Automation",
        detail: "Developed software in C for automation CAD software",
      },
    ],
  },
];

const education: Education[] = [
  {
    school: "National Taiwan University",
    url: "https://www.ntu.edu.tw/english/",
    location: "Taipei",
    degree: "MBA",
    date: "September 2020 – May 2022",
    focus:
      "Web Platform Strategies · Mergers and Acquisitions · Financial, Marketing and Strategic Management · Organizational Behavior · Negotiation Strategies and Skills · Leadership Practice",
  },
  {
    school: "University of British Columbia",
    url: "https://www.ubc.ca",
    location: "Vancouver",
    degree: "BASc, Computer Engineering",
    date: "September 2001 – May 2006",
    focus:
      "Software Engineering · Machine Learning · Nanochip Engineering · Embedded Software Development · First learned Java",
    minor:
      "Minor in Commerce — Accounting · Marketing · Economics · Venture Design",
  },
];

type Certification = { name: string; issuer: string; date?: string; url?: string };

const certifications: Certification[] = [
  {
    name: "PMP",
    issuer: "Project Management Institute",
  },
  {
    name: "CSPO — Certified Scrum Product Owner",
    issuer: "Scrum Alliance",
    date: "July 2023",
    url: "https://drive.google.com/file/d/1nYc_EvglMLKLHlBjqD7jJLf99NBqWxlr/view",
  },
  {
    name: "ITIL Foundation",
    issuer: "PeopleCert / Axelos",
    date: "November 2019",
    url: "https://drive.google.com/file/d/1Q4dMfGpZy-anRfV5UkdnJxMV523Y9kNm/view",
  },
  {
    name: "Crestron Certified Programmer",
    issuer: "Crestron",
    date: "September 2012",
    url: "https://drive.google.com/file/d/0BxxyF2_k9wiJTFBROG5GVkwzekk/view?resourcekey=0-bByfI7vWnAEf-bq_hJOAhg",
  },
  {
    name: "Essentials of Crestron Programming",
    issuer: "Crestron",
    date: "April 2008",
    url: "https://drive.google.com/file/d/0BxxyF2_k9wiJZ1REMEowRW4zVmM/view?resourcekey=0-QgStBImQknVjOc-WJGpokw",
  },
  {
    name: "Configuration of Crestron Systems",
    issuer: "Crestron",
    date: "April 2008",
    url: "https://drive.google.com/file/d/0BxxyF2_k9wiJZ1REMEowRW4zVmM/view?resourcekey=0-QgStBImQknVjOc-WJGpokw",
  },
];

type SkillSubgroup = { label: string; items: string[] };
type SkillGroup = {
  category: string;
  items?: string[];
  subgroups?: SkillSubgroup[];
};

const skillGroups: SkillGroup[] = [
  {
    category: "Coding Languages",
    items: ["JavaScript", "Python", "Solidity", "C#", "C++", "Java"],
  },
  {
    category: "Frameworks",
    items: ["React", "Node.js", "Next.js", "GraphQL"],
  },
  {
    category: "Infrastructure Systems",
    items: ["Postgres", "MySQL", "Azure", "Heroku", "Linux VPS"],
    subgroups: [
      {
        label: "AWS",
        items: [
          "EC2",
          "ELB",
          "Elastic Beanstalk",
          "DynamoDB",
          "CodeCommit",
          "IAM",
          "Lightsail",
          "Terraform",
        ],
      },
      {
        label: "Google Cloud",
        items: ["Cloud Run", "Cloud Build", "Firestore"],
      },
    ],
  },
  {
    category: "Tools & Analytics",
    items: [
      "Jira",
      "Notion",
      "Asana",
      "Salesforce",
      "Business analytics with Google/Firebase",
    ],
  },
];

const links = [
  { label: "LinkedIn", href: LINKEDIN_URL, text: "linkedin.com/in/waltyao" },
  { label: "GitHub", href: GITHUB_URL, text: "github.com/walter-kai" },
];

function ExternalIcon({ className = "w-3 h-3" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-9 4.5L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

/** Company/school name rendered as an external link when a URL is known. */
function OrgLink({
  name,
  url,
  className,
}: {
  name: string;
  url?: string;
  className?: string;
}) {
  if (!url) {
    return <span className={className}>{name}</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 hover:opacity-80 transition-opacity ${className ?? ""}`}
    >
      {name}
      <ExternalIcon className="w-3.5 h-3.5 opacity-70 shrink-0" />
    </a>
  );
}

/** Extract a short year range for timeline node labels. */
function yearRange(date: string): { start: string; end: string } {
  const years = date.match(/\d{4}/g) ?? [];
  const isCurrent = /current|present/i.test(date);
  return {
    start: years[0] ?? "",
    end: isCurrent ? "Now" : years[1] ?? years[0] ?? "",
  };
}

function Panel({
  title,
  children,
  delay,
}: {
  title: string;
  children: ReactNode;
  delay?: string;
}) {
  return (
    <section
      className="border border-white/15 rounded-xl p-5 bg-black/30 flex flex-col"
      style={{ animation: delay ? `cascadeIn 0.6s ease-out ${delay} backwards` : undefined }}
    >
      <h2
        className="font-plex-mono text-xs uppercase tracking-[0.16em] mb-4 pb-2 border-b border-white/15"
        style={{ color: ACCENT }}
      >
        {title}
      </h2>
      <div className="flex-1 min-h-0">{children}</div>
    </section>
  );
}

function JobDetail({ job }: { job: Job }) {
  return (
    <article
      key={job.company}
      style={{ animation: "cascadeIn 0.4s ease-out backwards" }}
    >
      <div className="flex items-center gap-3">
        {job.logo && (
          <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-lg bg-white p-1.5 ring-1 ring-black/5">
            <img
              src={job.logo}
              alt={`${job.company} logo`}
              className="max-h-full max-w-full object-contain"
              loading="lazy"
            />
          </span>
        )}
        <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
          <h3 className="text-xl font-bold text-white">
            <OrgLink name={job.company} url={job.url} />
            <span className="text-gray-400 font-normal text-base">
              {" "}
              — {job.location}
            </span>
          </h3>
          <span className="font-plex-mono text-xs text-gray-400 shrink-0">
            {job.date}
          </span>
        </div>
      </div>
      <p className="text-sm mt-0.5 font-medium" style={{ color: ACCENT }}>
        {job.title}
      </p>
      <ul className="mt-4 space-y-2">
        {job.bullets.map((bullet) => (
          <li
            key={bullet.label}
            className="text-gray-300 text-sm leading-relaxed flex gap-2"
          >
            <span
              className="mt-2 h-1 w-1 rounded-full shrink-0"
              style={{ backgroundColor: ACCENT }}
            />
            <span>
              <strong className="text-white font-semibold">
                {bullet.label}:
              </strong>{" "}
              {bullet.detail}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function CV() {
  const [isLoaded, setIsLoaded] = useState(false);

  // Timeline runs newest → oldest, left → right.
  const timeline = workExperience;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = workExperience[selectedIndex].company;

  // Scroll container + per-job anchors for the scroll-spy timeline.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const jobRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Timeline node buttons + progress line that fills up to the active node.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [fillWidth, setFillWidth] = useState(0);
  const isClickScrolling = useRef(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Highlight the job nearest the top of the scroll container as the user scrolls.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isClickScrolling.current) return;
      const marker = container.scrollTop + 100;
      let current = 0;
      jobRefs.current.forEach((el, i) => {
        if (el && el.offsetTop <= marker) current = i;
      });
      // Snap to the last job once scrolled to the bottom.
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 2
      ) {
        current = workExperience.length - 1;
      }
      setSelectedIndex(current);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Grow the accent line to the center of the active timeline node.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const node = nodeRefs.current[selectedIndex];
    if (!track || !node) return;
    const update = () => {
      const trackBox = track.getBoundingClientRect();
      const nodeBox = node.getBoundingClientRect();
      setFillWidth(nodeBox.left + nodeBox.width / 2 - trackBox.left);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [selectedIndex]);

  const scrollToJob = (index: number) => {
    const container = scrollRef.current;
    const el = jobRefs.current[index];
    if (!container || !el) return;
    setSelectedIndex(index);
    // Briefly suppress scroll-spy so the smooth scroll settles on the target.
    isClickScrolling.current = true;
    container.scrollTo({ top: el.offsetTop - 4, behavior: "smooth" });
    nodeRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
    window.setTimeout(() => {
      isClickScrolling.current = false;
    }, 600);
  };

  return (
    <main
      className="flex-1 w-full px-4 md:px-6 py-12 max-w-6xl mx-auto font-plex-sans"
      style={{
        animation: isLoaded ? "fadeIn 0.6s ease-out" : "fadeIn 0.6s ease-out",
      }}
    >
      {/* Header */}
      <header
        className="border border-white/15 rounded-xl p-6 md:p-8 mb-8 bg-black/30"
        style={{ animation: "cascadeIn 0.6s ease-out 0.15s backwards" }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 md:gap-8">
          <img
            src="/assets/walt-bust.png"
            alt="Walt Yao"
            className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover object-center border-2 shrink-0 mx-auto sm:mx-0"
            style={{ borderColor: ACCENT }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Walt Yao
                </h1>
                <p
                  className="font-plex-mono text-sm md:text-base mt-1 tracking-wide"
                  style={{ color: ACCENT }}
                >
                  BASc, MBA, PMP
                </p>
                <p className="text-gray-200 mt-3 text-lg leading-snug">
                  Software Engineering | Product Management
                </p>
                <p className="text-gray-400 text-base leading-snug">
                  Developer Experience | Technical Consulting
                </p>
                <div className="mt-3 flex flex-wrap gap-2" aria-label="Languages">
                  {["Eng", "Fra", "中文"].map((lang) => (
                    <span
                      key={lang}
                      className="font-plex-mono text-[11px] tracking-wide px-2.5 py-1 rounded-full border border-white/20 text-gray-300"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-stretch sm:items-end gap-3 shrink-0 self-start">
                <a
                  href="/resume/walt-yao-resume.pdf"
                  download
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-white/5"
                  style={{ borderColor: ACCENT, color: ACCENT }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download PDF
                </a>
                <div className="flex flex-col items-stretch sm:items-end gap-1.5">
                  {links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm hover:opacity-80 transition-opacity break-all"
                      style={{ color: ACCENT }}
                    >
                      {link.text}
                      <ExternalIcon className="w-3 h-3 opacity-60 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Education / Skills */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <Panel title="Education" delay="0.25s">
          <div className="flex h-full flex-col gap-7">
            {education.map((edu) => (
              <div key={edu.school}>
                <h3 className="text-lg font-bold text-white leading-snug">
                  <OrgLink name={edu.school} url={edu.url} />
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  {edu.location} —{" "}
                  <span style={{ color: ACCENT }}>{edu.degree}</span>
                </p>
                <p className="font-plex-mono text-xs text-gray-500 mt-1">
                  {edu.date}
                </p>
                <p className="text-sm text-gray-300 mt-2.5 leading-relaxed">
                  {edu.focus}
                </p>
                {edu.minor && (
                  <p
                    className="text-sm mt-2 leading-relaxed font-medium"
                    style={{ color: ACCENT }}
                  >
                    {edu.minor}
                  </p>
                )}
              </div>
            ))}

            <div className="mt-auto border-t border-white/10 pt-6">
              <h3 className="font-plex-mono text-xs uppercase tracking-[0.15em] text-gray-500 mb-4">
                Certifications
              </h3>
              <ul className="space-y-3">
                {certifications.map((cert) => (
                  <li
                    key={cert.name}
                    className="flex items-baseline justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-200 leading-snug">
                        {cert.url ? (
                          <OrgLink name={cert.name} url={cert.url} />
                        ) : (
                          cert.name
                        )}
                      </p>
                      <p className="text-xs text-gray-500 leading-snug">
                        {cert.issuer}
                      </p>
                    </div>
                    {cert.date && (
                      <span className="font-plex-mono text-xs text-gray-500 shrink-0">
                        {cert.date}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Panel>

        <Panel title="Skills" delay="0.3s">
          <div className="space-y-5">
            {skillGroups.map((group) => (
              <div key={group.category}>
                <h3 className="text-sm font-bold text-white leading-snug">
                  {group.category}
                </h3>
                {group.items && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {group.items.map((item) => (
                      <span
                        key={item}
                        className="font-plex-mono text-[11px] leading-tight px-2.5 py-1 rounded-full border border-white/15 text-gray-300 bg-white/[0.03]"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                )}
                {group.subgroups?.map((sub) => (
                  <div key={sub.label} className="mt-2.5">
                    <p className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-gray-500 mb-1.5">
                      {sub.label}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {sub.items.map((item) => (
                        <span
                          key={item}
                          className="font-plex-mono text-[11px] leading-tight px-2.5 py-1 rounded-full border border-white/15 text-gray-300 bg-white/[0.03]"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Work Experience — full width to match header */}
      <div style={{ animation: "cascadeIn 0.6s ease-out 0.35s backwards" }}>
        <Panel title="Work Experience">
          {/* Timeline — click a node to jump; highlights as you scroll */}
          <div className="overflow-x-auto -mx-1 px-1 pb-3">
            <div ref={trackRef} className="relative flex min-w-max gap-2 pt-1">
              {/* base connecting line */}
              <div className="absolute left-0 right-0 top-[9px] h-px bg-white/15" />
              {/* accent fill that grows to the active node */}
              <div
                className="absolute left-0 top-[9px] h-px transition-[width] duration-300 ease-out"
                style={{ width: fillWidth, backgroundColor: ACCENT }}
              />
              {timeline.map((job, index) => {
                const active = job.company === selected;
                const visited = index <= selectedIndex;
                const { start, end } = yearRange(job.date);
                return (
                  <button
                    key={job.company}
                    type="button"
                    ref={(el) => {
                      nodeRefs.current[index] = el;
                    }}
                    onClick={() => scrollToJob(index)}
                    aria-pressed={active}
                    className="relative z-10 flex flex-col items-center gap-1.5 px-3 pt-0 text-center group focus:outline-none"
                  >
                    <span
                      className="h-[14px] w-[14px] rounded-full border-2 transition-all bg-[#0b0f17]"
                      style={{
                        borderColor: visited ? ACCENT : "rgba(255,255,255,0.3)",
                        backgroundColor: active
                          ? ACCENT
                          : visited
                            ? "rgba(242,184,75,0.35)"
                            : "#0b0f17",
                        boxShadow: active
                          ? `0 0 0 4px rgba(242,184,75,0.2)`
                          : "none",
                      }}
                    />
                    <span
                      className="font-plex-mono text-[11px]"
                      style={{ color: active ? ACCENT : "#9ca3af" }}
                    >
                      {start}
                      {end && end !== start ? `–${end === "Now" ? "Now" : end.slice(2)}` : ""}
                    </span>
                    <span
                      className={`text-xs font-semibold leading-tight max-w-[7rem] transition-colors ${
                        active ? "text-white" : "text-gray-400 group-hover:text-gray-200"
                      }`}
                    >
                      {job.company}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scrollable job list — timeline above tracks the scroll position */}
          <div
            ref={scrollRef}
            className="relative mt-6 pt-6 border-t border-white/10 max-h-[70vh] overflow-y-auto pr-1 space-y-8 scroll-smooth"
          >
            {workExperience.map((job, index) => (
              <div
                key={job.company}
                ref={(el) => {
                  jobRefs.current[index] = el;
                }}
              >
                <JobDetail job={job} />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-12 text-center text-gray-500 text-sm border-t border-white/10 pt-8">
        <p>© {new Date().getFullYear()} Walt Yao. All rights reserved.</p>
      </div>
    </main>
  );
}

export default CV;
